import React, { useRef, useEffect } from 'react';

import { X } from 'lucide-react';
import ReactDOM from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  actions,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, onClose]);

  useEffect(() => {
    const html = document.querySelector('html');

    if (html) {
      html.style.overflowY = isOpen ? 'hidden' : 'visible';
    }

    return (): void => {
      if (html) {
        html.style.overflowY = 'visible';
      }
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className="fixed top-0 left-0 z-20 flex items-center justify-center w-screen h-screen bg-slate-300/20 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="flex max-h-[90vh] w-11/12 max-w-2xl flex-col gap-6 overflow-hidden rounded bg-white p-6 text-slate-500 shadow-xl shadow-slate-700/10"
        ref={wrapperRef}
      >
        {/* Modal Header */}
        {title && (
          <header className="flex items-center gap-4">
            <h3 className="flex-1 text-sm font-medium text-slate-700">
              {title}
            </h3>
            <div className="flex items-center gap-2">
              {actions}
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center h-10 gap-2 px-3 text-sm font-medium tracking-wide cursor-pointer
                transition duration-300 rounded-full justify-self-center whitespace-nowrap
                 text-secondary hover:bg-orange-200 hover:text-secondary focus:bg-orange-200 focus:text-secondary focus-visible:outline-none"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </header>
        )}

        {/* Modal Body */}
        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
